<!--DEPRECATED in favor of ButtonV2 -->
<template>
  <button
    ref="buttonRef"
    :class="[
      'rounded-md text-left text-base font-bold transition-all',
      props.loading ? 'cursor-not-allowed' : '',
      props.disabled
        ? 'cursor-not-allowed bg-neutral-200 text-neutral-400'
        : classMap[props.type] && classMap[props.type][props.color]
          ? classMap[props.type][props.color]
          : '',
      props.dense ? 'px-2 py-1' : 'px-2 py-2 sm:px-3',
      props.bounce ? 'bounce-button' : '',
    ]"
    :disabled="props.disabled || props.loading"
  >
    <div :class="breakpoints.showAfterBreakpointClasses">
      <div
        :class="[
          'flex items-center justify-center',
          props.dense ? 'gap-1' : 'gap-2',
        ]"
      >
        <template v-if="!iconAfterText">
          <TIcon
            v-if="loading"
            name="loader"
            class="animate-spin py-1"
            :size="iconSize"
          />
          <TIcon v-if="!loading && icon" :name="icon" :size="iconSize" />
        </template>

        <slot />

        <template v-if="iconAfterText">
          <TIcon
            v-if="loading"
            name="loader"
            class="animate-spin py-1"
            :size="iconSize"
          />
          <TIcon v-if="!loading && icon" :name="icon" :size="iconSize" />
        </template>
      </div>
    </div>

    <div :class="breakpoints.hideAfterBreakpointClasses">
      <Tippy theme="teachino-tippy" :arrow="false" placement="bottom">
        <template #default>
          <TIcon v-if="loading" name="loader" class="animate-spin py-1" />
          <TIcon v-if="!loading && icon" :name="icon" :size="iconSize" />
        </template>
        <template #content>
          <slot />
        </template>
      </Tippy>
    </div>
  </button>
</template>
<script setup lang="ts">
import { Tippy } from 'vue-tippy';
import type { IconType } from '../Icon/icon';
import TIcon from '../Icon/TIcon.vue';
import type { ButtonColor, ButtonType, IconBreakpoint } from './button-types';
import { calculateBreakpoints } from './calculate-breakpoints';
import { computed, nextTick, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    type: ButtonType;
    color: ButtonColor;
    disabled?: boolean;
    loading?: boolean;
    icon?: IconType;
    iconOnly?: IconBreakpoint;
    dense?: boolean;
    iconAfterText?: boolean;
    bounce?: boolean;
    /**
     * Move keyboard focus to this button when it mounts. Used by confirm
     * dialogs so Enter triggers the action instead of leaking back to the
     * control that opened the dialog.
     */
    autofocus?: boolean;
  }>(),
  {
    disabled: false,
    loading: false,
    icon: undefined,
    iconOnly: undefined,
    dense: false,
    iconAfterText: false,
    bounce: false,
    autofocus: false,
  },
);

const buttonRef = ref<HTMLButtonElement | null>(null);

onMounted(() => {
  if (!props.autofocus) {
    return;
  }

  void nextTick(() => {
    buttonRef.value?.focus();
  });
});

const breakpoints = calculateBreakpoints(props.iconOnly);

const tonalVariantClassMap: Record<ButtonColor, string> = {
  primary:
    'line-clamp-1 bg-primary-50 enabled:hover:bg-primary-100 text-primary-600',
  secondary:
    'line-clamp-1 bg-secondary-50 enabled:hover:bg-secondary-100 text-secondary-600',
  success:
    'line-clamp-1 bg-success-50 enabled:hover:bg-success-100 text-success-500',
  danger:
    'line-clamp-1 bg-danger-50 enabled:hover:bg-danger-100 text-danger-500',
  neutral:
    'line-clamp-1 bg-neutral-50 enabled:hover:bg-neutral-200 text-neutral-500 hover:text-neutral-800',
  tertiary:
    'line-clamp-1 bg-tertiary-50 enabled:hover:bg-tertiary-100 text-tertiary-500',
  quarternary:
    'line-clamp-1 bg-quarternary-50 enabled:hover:bg-quarternary-100 text-quarternary-500',
};

const filledVariantClassMap: Record<ButtonColor, string> = {
  primary:
    'line-clamp-1 text-white bg-primary-500 enabled:hover:bg-primary-600',
  secondary:
    'line-clamp-1 text-white bg-secondary-600 enabled:hover:bg-secondary-700',
  success:
    'line-clamp-1 text-white bg-success-500 enabled:hover:bg-success-800',
  danger: 'line-clamp-1 text-white bg-danger-500 enabled:hover:bg-danger-800',
  neutral:
    'line-clamp-1 text-white bg-neutral-500 enabled:hover:bg-neutral-800',
  tertiary:
    'line-clamp-1 text-white bg-tertiary-800 enabled:hover:bg-tertiary-800',
  quarternary:
    'line-clamp-1 text-white bg-quarternary-500 enabled:hover:bg-quarternary-800',
};

const textVariantClassMap: Record<ButtonColor, string> = {
  primary: 'inline text-primary-500 enabled:hover:bg-primary-50',
  secondary: 'inline text-secondary-500 enabled:hover:bg-secondary-50',
  success: 'inline text-success-500 enabled:hover:bg-success-50',
  danger: 'inline text-danger-500 enabled:hover:bg-danger-50',
  neutral: 'text-neutral-800 enabled:hover:bg-neutral-50',
  tertiary: 'inline text-tertiary-500 enabled:hover:bg-tertiary-50',
  quarternary: 'inline text-quarternary-500 enabled:hover:bg-quarternary-50',
};

const classMap: Record<ButtonType, Record<ButtonColor, string>> = {
  tonal: tonalVariantClassMap,
  filled: filledVariantClassMap,
  text: textVariantClassMap,
};

const iconSize = computed(() => {
  return props.dense ? '20' : '24';
});
</script>
<style scoped>
@keyframes bounce-button {
  0%,
  100% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
  50% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
}
.bounce-button {
  animation: bounce-button 0.5s 2 ease-out;
}
</style>
